import { _t } from '@/i18n/t';
import { SUPPORT_EMAIL } from '@/shared/adapters/constants';
import { getToastAPI } from '@/shared/components/toast-system';

interface ApiErrorShape {
  response?: { data?: { error?: unknown; message?: unknown; isForUser?: unknown; errorCode?: unknown } };
  message?: unknown;
  errorCode?: unknown;
  isAxiosError?: unknown;
}

/** A failure read off a streamed response, shaped so the readers below treat it like an HTTP error. */
export class StreamRequestError extends Error {
  status?: number;
  errorCode?: string;
  isForUser?: boolean;

  constructor(message: string, details: { status?: number; errorCode?: string; isForUser?: boolean } = {}) {
    super(message);
    this.name = 'StreamRequestError';
    this.status = details.status;
    this.errorCode = details.errorCode;
    this.isForUser = details.isForUser;
  }
}

export const extractApiError = (error: unknown, fallback: string): string => {
  const shaped = error as ApiErrorShape | null;
  const data = shaped?.response?.data;

  const candidates = [data?.error, data?.message, shaped?.message];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }

  return fallback;
};

export const extractUserFacingError = (error: unknown, fallback: string): string => {
  const shaped = error as ApiErrorShape | null;

  // The backend sets isForUser only on messages that are safe to show; anything
  // else is technical and must not reach the user.
  if (shaped?.isAxiosError) {
    const data = shaped.response?.data;
    if (data?.isForUser === true && typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
    return fallback;
  }

  // A background job rejects with the message it recorded, already user-facing.
  const message = shaped?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

const AI_LIMIT_MESSAGE_KEYS: Record<string, string> = {
  AI_BUDGET_EXCEEDED: 'ai.limit.budgetExceeded',
  AI_TRIAL_DAILY_CAP: 'ai.limit.dailyCap',
  AI_SUSPENDED: 'ai.limit.suspended',
};

export const getAiLimitMessage = (error: unknown): string | null => {
  const shaped = error as ApiErrorShape | null;
  const code = shaped?.response?.data?.errorCode ?? shaped?.errorCode;
  const key = typeof code === 'string' ? AI_LIMIT_MESSAGE_KEYS[code] : undefined;
  return key ? _t(key) : null;
};

const AI_LIMIT_CONTACT_CODES = new Set(['AI_BUDGET_EXCEEDED', 'AI_TRIAL_DAILY_CAP', 'AI_SUSPENDED']);

export const getAiLimitContactHref = (): string =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(_t('ai.limit.contactSubject'))}`;

export const notifyAiLimit = (error: unknown): boolean => {
  const shaped = error as ApiErrorShape | null;
  const code = shaped?.response?.data?.errorCode ?? shaped?.errorCode;
  const message = getAiLimitMessage(error);
  if (typeof code !== 'string' || !AI_LIMIT_CONTACT_CODES.has(code) || !message) return false;

  getToastAPI()?.show({
    type: 'error-hard',
    title: message,
    dedupeKey: 'ai-limit',
    action: {
      label: 'Contact us',
      onClick: () => {
        window.location.href = getAiLimitContactHref();
      },
    },
  });
  return true;
};

type GenericHttpErrorReason = 'notFound' | 'timeout' | 'unavailable';

const GENERIC_HTTP_ERROR_REASONS: Record<number, GenericHttpErrorReason> = {
  404: 'notFound',
  502: 'unavailable',
  503: 'unavailable',
  504: 'timeout',
};

const getGenericHttpStatusReason = (
  status: unknown,
  backendMessage: unknown,
): GenericHttpErrorReason | null => {
  if (typeof backendMessage === 'string' && backendMessage.trim()) return null;
  if (typeof status !== 'number') return null;
  return GENERIC_HTTP_ERROR_REASONS[status] ?? null;
};

export const getGenericHttpStatusMessage = (
  status: unknown,
  backendMessage: unknown,
  action: string,
): string | null => {
  const reason = getGenericHttpStatusReason(status, backendMessage);
  if (!reason) return null;

  return _t('publish.tab.error.serviceUnavailableWithReason', {
    action,
    reason: _t(`publish.tab.error.reason.${reason}`),
  });
};

export const extractMissingScopes = (error: unknown): string[] => {
  const data = (error as { response?: { data?: { missingScopes?: unknown } } } | null)?.response?.data;
  const scopes = data?.missingScopes;

  if (!Array.isArray(scopes)) return [];

  return scopes.filter((scope): scope is string => typeof scope === 'string');
};
